import AddButton from "../components/AddButton";
import Sidebar from "../components/Sidebar";

import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {privateApi} from "../api";
import DateTimeSpan from "../components/DateTimeSpan";
import OrderStatusSpan from "../components/OrderStatusSpan";
import RefreshButton from "../components/RefreshButton";
import {useNavigate} from "react-router-dom";

type OrderObject = {
    id: string;
    customer: string;
    device: string;
    brand: string;
    status: string;
    creationTimestamp: string;
};

type OrdersResponse = {
    orders: OrderObject[];
    total: number;
};

type Cliente = {
    id: number;
    name: string;
    surname: string;
    phone: string;
};

type CustomerResponse = {
    customers: Cliente[];
};

type Device = {
    id: number;
    model: string;
    brand: string;
};

type DeviceResponse = {
    devices: Device[];
}

const PER_PAGE = 25;

function Order() {
    localStorage.setItem("focus", "ordini")
    const [orderBy, setOrderBy] = useState("creation_timestamp@asc");
    const [filters, setFilters] = useState({
        inProgress: true,
        delivered: false,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [orders, setOrders] = useState<OrderObject[]>([]);
    const [page, setPage] = useState(0);

    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    // ---- CLIENTE ----
    const [clienteInput, setClienteInput] = useState("");
    const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
    const [clienteResults, setClienteResults] = useState<Cliente[]>([]);
    const [showClienteDropdown, setShowClienteDropdown] = useState(false);

    // ---- DEVICE ----
    const [deviceInput, setDeviceInput] = useState("");
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [deviceResults, setDeviceResults] = useState<Device[]>([]);
    const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);

    // ----- Description ---
    const [description, setDescription] = useState("");

    //Filter
    const [filter, setFilter] = useState("");

    const clienteRef = useRef<HTMLDivElement>(null);
    const deviceRef = useRef<HTMLDivElement>(null);

    const clienteCancelRef = useRef<AbortController | null>(null);
    const deviceCancelRef = useRef<AbortController | null>(null);

    const totalPages = Math.ceil(total / PER_PAGE);

    // ---- OUTSIDE CLICK ----
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!clienteRef.current?.contains(e.target as Node)) {
                setShowClienteDropdown(false);
            }
            if (!deviceRef.current?.contains(e.target as Node)) {
                setShowDeviceDropdown(false);
            }
        };

        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    // ---- CLIENTE SEARCH ----
    useEffect(() => {
        if (!clienteInput) {
            setClienteResults([]);
            return;
        }

        const timeout = setTimeout(async () => {
            try {
                clienteCancelRef.current?.abort();

                const controller = new AbortController();
                clienteCancelRef.current = controller;

                const res = await privateApi.get<CustomerResponse>("/cr-order-manager/v1/customer/search",
                    {
                        params: {search: clienteInput},
                        signal: controller.signal,
                    }
                );

                setClienteResults(res.data.customers ?? []);
                setShowClienteDropdown(true);
            } catch {
                setClienteResults([]);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [clienteInput]);

    // ---- DEVICE SEARCH ----
    useEffect(() => {
        if (!deviceInput) {
            setDeviceResults([]);
            return;
        }

        const timeout = setTimeout(async () => {
            try {
                deviceCancelRef.current?.abort();

                const controller = new AbortController();
                deviceCancelRef.current = controller;

                const res = await privateApi.get<DeviceResponse>("/cr-order-manager/v1/device/search", {
                    params: {search: deviceInput},
                    signal: controller.signal,
                });

                setDeviceResults(res.data.devices ?? []);
                setShowDeviceDropdown(true);
            } catch {
                setDeviceResults([]);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [deviceInput]);

    // ---- FETCH ORDERS ----
    const loadOrders = async () => {
        try {
            setLoading(true);

            const res = await privateApi.get<OrdersResponse>(
                `/cr-order-manager/v1/order?page=${page}&perPage=${PER_PAGE}&orderBy=${orderBy}&inProgress=${filters.inProgress}&delivered=${filters.delivered}&filter=${filter}`
            );

            setOrders(res.data.orders);
            setTotal(res.data.total);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedCliente) return toast.error("Seleziona un cliente valido");
        if (!selectedDevice) return toast.error("Seleziona un device valido");
        if (!description) return toast.error("Inserisci una descrizione");

        const payload = {
            customerId: selectedCliente.id,
            deviceId: selectedDevice.id,
            description: description,
            workTypeIds: []
        };

        try {
            const res = await privateApi.post("/cr-order-manager/v1/order", payload);

            navigate(`/order/${res.data.orderId}`);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    // useEffect(() => {
    //     loadOrders();
    // }, [page, orderBy, filters]);

    useEffect(() => {
        setPage(0);
    }, [filters, orderBy]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            loadOrders();
        }, 100);

        return () => clearTimeout(timeout);
    }, [page, orderBy, filters, filter]);

    const resetForm = () => {
        setClienteInput("");
        setSelectedCliente(null);
        setClienteResults([]);

        setDeviceInput("");
        setSelectedDevice(null);
        setDeviceResults([]);

        setDescription("");

        setShowClienteDropdown(false);
        setShowDeviceDropdown(false);
    }
    const navigate = useNavigate();

    return (
        <>
            <Sidebar/>

            <main className="ml-64 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Ordini</h1>
                    <div className="flex items-center gap-2">
                        <RefreshButton onClick={loadOrders}/>
                        <AddButton
                            onClick={() => {
                                resetForm();
                                setIsModalOpen(true);
                            }}
                        />
                    </div>
                </div>

                {/* FILTERS */}
                <div className="mb-6 bg-white p-4 rounded-xl shadow">
                    <div className="flex items-center justify-between">
                        <h2 className="font-semibold">Filtri</h2>

                        <span className="text-sm text-gray-500">Totale ordini: {total}</span>
                    </div>


                    {/*FILTER HEADER */}
                    <div className="border-t pt-3 flex items-center justify-between mb-3">

                        {/*ORDER BY*/}
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Ordina per:</label>

                            <select
                                value={orderBy}
                                onChange={(e) => {
                                    setOrderBy(e.target.value);
                                    setPage(0);
                                }}
                                className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900">
                                <option value="creation_timestamp@asc">Data (meno recente)</option>
                                <option value="creation_timestamp@esc">Data (più recente)</option>
                                <option value="customer_name@asc">Cliente</option>
                                <option value="order_status@asc">Stato</option>
                            </select>
                        </div>

                    </div>

                    {/*FILTER CONTENT*/}
                    <div id="filterMenu" className="border-t pt-3">

                        <p className="text-sm font-medium text-gray-700 mb-2">Stato ordine</p>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">

                            <label className="flex items-center gap-2">
                                <input type="checkbox"
                                       checked={filters.inProgress}
                                       onChange={(e) =>
                                           setFilters((prev) => ({
                                               ...prev,
                                               inProgress: e.target.checked,
                                           }))
                                       }
                                       className="accent-blue-900"/>
                                In corso
                            </label>

                            <label className="flex items-center gap-2">
                                <input type="checkbox"
                                       checked={filters.delivered}
                                       onChange={(e) =>
                                           setFilters((prev) => ({
                                               ...prev,
                                               delivered: e.target.checked,
                                           }))
                                       }
                                       className="accent-blue-900"/>
                                Consegnato
                            </label>

                        </div>

                    </div>

                </div>

                <div className="mb-6">
                    <input
                        type="text"
                        placeholder="Cerca ordine..."
                        className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={filter}
                        onChange={(e) => {
                            setFilter(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>

                {/* TABLE */}
                <div className="bg-white rounded-xl shadow p-5">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-gray-500 border-b">
                            <tr>
                                <th className="py-2">ID</th>
                                <th className="py-2">Cliente</th>
                                <th className="py-2">Device</th>
                                <th className="py-2">Stato</th>
                                <th className="py-2">Data</th>
                            </tr>
                            </thead>

                            <tbody className="divide-y">
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="py-6 text-center text-gray-500"
                                    >
                                        Caricamento...
                                    </td>
                                </tr>
                            ) : orders.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="py-6 text-center text-gray-500"
                                    >
                                        Nessun ordine trovato
                                    </td>
                                </tr>
                            ) : (
                                orders.map((o) => (
                                    <tr key={o.id} className="hover:bg-gray-50"
                                        onClick={() => {
                                            // localStorage.setItem("focus", "ordini")
                                            navigate(`/order/${o.id}`)
                                        }}>
                                        <td className="py-2">{o.id}</td>
                                        <td>{o.customer}</td>
                                        <td>{o.brand + " " + o.device}</td>
                                        <td><OrderStatusSpan status={o.status}/></td>
                                        <td><DateTimeSpan date={o.creationTimestamp}/></td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    <div className="flex justify-between items-center mt-4">
                        <button
                            onClick={() => setPage((p) => Math.max(p - 1, 0))}
                            disabled={page === 0}
                            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                        >
                            Prev
                        </button>

                        <span className="text-sm">Pagina {page + 1} di {totalPages || 1}</span>

                        <button
                            onClick={() =>
                                setPage((p) => Math.min(p + 1, totalPages - 1))
                            }
                            disabled={page >= totalPages - 1}
                            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">
                                Nuovo Ordine
                            </h2>

                            <div className="space-y-3">
                                {/* CLIENTE */}
                                <div ref={clienteRef} className="relative">
                                    <input
                                        value={clienteInput}
                                        onChange={(e) => {
                                            setClienteInput(e.target.value);
                                            setSelectedCliente(null);
                                        }}
                                        className="w-full border p-2 rounded"
                                        placeholder="Cliente*"
                                    />

                                    {showClienteDropdown &&
                                        clienteResults.length > 0 && (
                                            <div
                                                className="absolute z-10 w-full bg-white border rounded mt-1 max-h-40 overflow-y-auto shadow">
                                                {clienteResults.map((c) => (
                                                    <div
                                                        key={c.id}
                                                        onClick={() => {
                                                            setClienteInput(
                                                                `${c.name} ${c.surname}`
                                                            );
                                                            setSelectedCliente(c);
                                                            setShowClienteDropdown(false);
                                                        }}
                                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        {c.name} {c.surname}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                </div>

                                {/* DEVICE */}
                                <div ref={deviceRef} className="relative">
                                    <input
                                        value={deviceInput}
                                        onChange={(e) => {
                                            setDeviceInput(e.target.value);
                                            setSelectedDevice(null);
                                        }}
                                        className="w-full border p-2 rounded"
                                        placeholder="Device*"
                                    />

                                    {showDeviceDropdown &&
                                        deviceResults.length > 0 && (
                                            <div
                                                className="absolute z-10 w-full bg-white border rounded mt-1 max-h-40 overflow-y-auto shadow">
                                                {deviceResults.map((d) => (
                                                    <div
                                                        key={d.id}
                                                        onClick={() => {
                                                            setDeviceInput(d.model);
                                                            setSelectedDevice(d);
                                                            setShowDeviceDropdown(false);
                                                        }}
                                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        {d.brand + " " + d.model}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                </div>


                                <textarea className="w-full border p-2 rounded"
                                          placeholder="Descrizione*"
                                          value={description}
                                          rows={5}
                                          cols={40}
                                          onChange={(e) => {
                                              setDescription(e.target.value);
                                          }}/>

                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded"
                                >
                                    Annulla
                                </button>

                                <button
                                    onClick={handleSubmit}
                                    className="px-4 py-2 bg-blue-900 text-white rounded"
                                >
                                    Salva
                                </button>
                            </div>

                            <span>(*) Campo obbligatorio</span>
                        </div>
                    </div>
                )}
            </main>
        </>
    );
}

export default Order;